// De vaste gegevens van een huishouden: welke er zijn, welke vorm ze hebben,
// en hoe je ze leest.
//
// WAAROM DIT BESTAAT. Tot nu toe stond alles wat over een huishouden bekend is
// in `PersonaData`: één document per veld met een vrije titel. De code las een
// deel daarvan op die titel (adres, naam, e-mail, telefoon, aantal bewoners)
// en ging stil stuk zodra een organisatie een titel anders spelde of
// verwijderde. Voor de SiSa/SPUK-verantwoording ontbrak bovendien een vast
// veld voor huur of koop: 'Type woning' bevatte in productie woningvorm en
// eigendom door elkaar. Deze velden staan voortaan op het `Adresses`-document
// zelf, met een vaste naam en een vaste waardelijst.
// AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md
//
// DE OVERGANG. Tot de migratie (T15/T16) staat de waarde bij de meeste
// huishoudens nog in `PersonaData`. `readHouseholdField` leest daarom eerst
// het veld op het huishouden en valt anders terug op de oude titels. Zodra
// alles is overgezet gaat die terugval eruit (T17). Lees een vast veld dus
// NOOIT zelf op titel — `householdFields.guard.test.js` telt dat.
//
// HET ADRES IS DE INVOER, NIET `Location`. `Location` is wat PDOK teruggaf, en
// dat wijkt af bij 'ander adres gevonden', 'meerdere woningen' of 'niet
// gevonden'. De velden hier zijn wat de gebruiker invulde; `Location` blijft de
// uitkomst van de opzoeking, gekoppeld via `Location.GeocodedFrom`.
//
// Gespiegeld in functions/adressHelpers/householdFields.js. De frontend is de
// bron; functions/adressHelpers/householdFields.test.js houdt ze gelijk.

import resolveHouseholdEmail from './householdEmail'
import normalizeEmail from '../normalizeEmail'
import { sentinelToEmpty } from '../sentinelValue'
import readResidentCount from './readResidentCount'

/**
 * @typedef {object} FieldOption
 * @property {string} value - Opgeslagen sleutel
 * @property {string} label - Wat de gebruiker ziet
 */

/**
 * Eigendom van de woning. Voor de gebruiker heet het veld 'Type woning', met
 * de woorden die de FIXtool-intake al gebruikte; in de database en in de
 * SiSa/SPUK-verantwoording is het eigendom (huur of koop). Besluit user
 * 2026-10-28. De sleutels veranderen niet.
 * @type {Array<FieldOption>}
 */
const EIGENDOM_OPTIONS = [
    { value: 'koop', label: 'Koop' },
    { value: 'huur-corporatie', label: 'Sociale huur' },
    { value: 'huur-particulier', label: 'Particuliere huur' },
]

/** @type {Array<FieldOption>} */
const WONINGVORM_OPTIONS = [
    { value: 'appartement', label: 'Appartement' },
    { value: 'tussenwoning', label: 'Tussenwoning' },
    { value: 'eindwoning', label: 'Eindwoning' },
    { value: '2-onder-1-kap', label: '2-onder-1 kap' },
    { value: 'vrijstaand', label: 'Vrijstaand' },
]

/**
 * @typedef {object} HouseholdFieldDef
 * @property {'contact'|'woning'} group - Blok op de kaart 'Vaste gegevens'
 * @property {'text'|'email'|'number'|'enum'} kind
 * @property {string} label - Label op de kaart 'Vaste gegevens'
 * @property {Array<string>} legacyTitles - Titels in PersonaData die dit veld vervangt, op volgorde van voorkeur
 * @property {Array<FieldOption>} [options] - Alleen bij `enum`
 */

/**
 * De naam van het veld is meteen de veldnaam op `Adresses`.
 * @type {Record<string, HouseholdFieldDef>}
 */
const HOUSEHOLD_FIELDS = {
    Naam: { group: 'contact', kind: 'text', label: 'Naam', legacyTitles: ['Voornaam', 'Achternaam', 'Naam'] },
    Email: { group: 'contact', kind: 'email', label: 'E-mail', legacyTitles: ['E-mail', 'Email', 'E-mailadres', 'Emailadres'] },
    Telefoon: { group: 'contact', kind: 'text', label: 'Telefoon', legacyTitles: ['Telefoonnummer', 'Telefoon', 'Mobiel'] },
    Postcode: { group: 'contact', kind: 'text', label: 'Postcode', legacyTitles: ['Postcode'] },
    Huisnummer: { group: 'contact', kind: 'text', label: 'Huisnummer', legacyTitles: ['Huisnummer'] },
    Toevoeging: { group: 'contact', kind: 'text', label: 'Toevoeging', legacyTitles: ['Toevoeging', 'Huisnummer'] },
    Straatnaam: { group: 'contact', kind: 'text', label: 'Straat', legacyTitles: ['Straatnaam'] },
    Woonplaats: { group: 'contact', kind: 'text', label: 'Woonplaats', legacyTitles: ['Woonplaats'] },
    AantalBewoners: { group: 'woning', kind: 'number', label: 'Aantal bewoners', legacyTitles: ['Aantal bewoners'] },
    Eigendom: {
        group: 'woning',
        kind: 'enum',
        label: 'Type woning',
        legacyTitles: ['Type woning', 'Eigendom woning', 'Eigendom', 'Woningbouwcooperatie'],
        options: EIGENDOM_OPTIONS,
    },
    Woningvorm: { group: 'woning', kind: 'enum', label: 'Woningvorm', legacyTitles: ['Woningsoort', 'Type woning'], options: WONINGVORM_OPTIONS },
    Woningcorporatie: { group: 'woning', kind: 'text', label: 'Woningcorporatie', legacyTitles: ['Woningbouwcooperatie'] },
}

/**
 * De oude titels die een label (straat + huisnummer, naam) nodig heeft zolang
 * de migratie niet gedraaid heeft. Voor een `in`-query op PersonaData; zonder
 * Voornaam/Achternaam valt een gesplitst opgeslagen naam stil weg.
 * @type {Array<string>}
 */
const LABEL_LEGACY_TITLES = [...new Set(['Straatnaam', 'Huisnummer', 'Toevoeging', 'Naam']
    .flatMap((field) => HOUSEHOLD_FIELDS[field].legacyTitles))]

/**
 * De oude titels voor een adreslabel met woonplaats ('Straat 12A, Plaats'),
 * voor de keuzelijst van huishoudens in een afspraak. Een vaste array, zodat
 * een hook die er een `in`-query mee opent niet bij elke render opnieuw begint.
 * @type {Array<string>}
 */
const ADDRESS_LABEL_TITLES = [...new Set(['Straatnaam', 'Huisnummer', 'Toevoeging', 'Woonplaats']
    .flatMap((field) => HOUSEHOLD_FIELDS[field].legacyTitles))]

const keyOf = (title) => String(title || '').trim().toLowerCase()

/**
 * Alle bruikbare waarden per titel (kleine letters), in de volgorde van de
 * regels. Dezelfde titel kan meermaals voorkomen, en een lege eerste regel mag
 * een gevulde tweede niet wegdrukken.
 * @param {Array<{Title?: string, Value?: string}>} rows
 * @returns {Map<string, Array<string>>}
 */
const valuesByTitle = (rows) => {
    const map = new Map()
    rows.forEach((row) => {
        const value = sentinelToEmpty(row?.Value)
        if (!value) return
        const key = keyOf(row?.Title)
        map.set(key, [...(map.get(key) || []), value])
    })
    return map
}

/**
 * De eerste bruikbare waarde onder één van de titels, in de volgorde van de
 * titels. Nooit samenvoegen: twee telefoonvelden zijn twee nummers.
 * @param {Map<string, Array<string>>} byTitle
 * @param {Array<string>} titles
 * @returns {string}
 */
const firstValue = (byTitle, titles) => {
    for (const title of titles) {
        const found = (byTitle.get(keyOf(title)) || [])[0]
        if (found) return found
    }
    return ''
}

/**
 * Splitst een huisnummer in nummer en toevoeging, en houdt de toevoeging zoals
 * hij op de deur staat. Bewust niet `splitHuisnummer` uit
 * `pdok/verifyAddressMatch`: die normaliseert de toevoeging om te VERGELIJKEN
 * (streepje weg, hoofdletters), en '12-1' zonder streepje leest als 121. Hier
 * gaat het om wat we OPSLAAN.
 * @param {string} value - Bijvoorbeeld '12A', '12-1', '7 h'
 * @returns {{nummer: string, toevoeging: string}}
 */
const splitHouseNumber = (value) => {
    const text = String(value || '').trim()
    const match = text.match(/^(\d+)\s*-?\s*(.*)$/)
    if (!match) return { nummer: text, toevoeging: '' }
    return { nummer: match[1], toevoeging: match[2].trim() }
}

/** Waarden bij 'Woningbouwcooperatie' die geen corporatie noemen. */
const NO_CORPORATION = new Set(['ja', 'nee', 'nvt', 'n.v.t.', 'overig', 'onbekend', 'koop', 'koopwoning', 'geen'])

/**
 * Wat één waarde over eigendom zegt.
 * @param {string} title - Titel in kleine letters
 * @param {string} value
 * @returns {string|null} Een sleutel uit EIGENDOM_OPTIONS, 'huur' (onbeslisbaar) of null (zegt niets)
 */
const eigendomFromValue = (title, value) => {
    const text = keyOf(value)
    if (title === 'woningbouwcooperatie') {
        if (text === 'koop' || text === 'koopwoning') return 'koop'
        if (text === 'ja') return 'huur-corporatie'
        if (NO_CORPORATION.has(text)) return null
        // Een corporatienaam: wie bij Ymere of Pre Wonen woont, huurt.
        return 'huur-corporatie'
    }
    if (text.includes('particulier')) return 'huur-particulier'
    if (text.includes('sociale huur') || text.includes('corporatie')) return 'huur-corporatie'
    if (text.startsWith('koop') || text === 'eigen woning' || text === 'eigenaar') return 'koop'
    if (text === 'huur' || text === 'huurwoning') return 'huur'
    return null
}

/**
 * @typedef {object} EigendomClassification
 * @property {string} value - Sleutel uit EIGENDOM_OPTIONS, of '' als het niet vaststaat
 * @property {'ok'|'onbeslisbaar'|'leeg'|'conflict'} status
 */

/**
 * Leidt eigendom af uit de oude gegevensregels. 'Huur' zonder meer is
 * onbeslisbaar: corporatie of particulier raden we niet (besluit A, user
 * 2026-10-27). De migratie zet zulke huishoudens op een lijst voor de hand.
 * @param {Array<{Title?: string, Value?: string}>} rows - PersonaData van één huishouden
 * @returns {EigendomClassification}
 */
const classifyEigendom = (rows) => {
    const byTitle = valuesByTitle(Array.isArray(rows) ? rows : [])
    const decided = new Set()
    let sawHuur = false
    HOUSEHOLD_FIELDS.Eigendom.legacyTitles.forEach((title) => {
        (byTitle.get(keyOf(title)) || []).forEach((value) => {
            const found = eigendomFromValue(keyOf(title), value)
            if (found === 'huur') sawHuur = true
            else if (found) decided.add(found)
        })
    })
    // Een onbeslisbare 'huur' naast een corporatienaam is gewoon huur bij een corporatie.
    if (decided.size === 1) return { value: [...decided][0], status: 'ok' }
    if (decided.size > 1) return { value: '', status: 'conflict' }
    return { value: '', status: sawHuur ? 'onbeslisbaar' : 'leeg' }
}

/**
 * @param {string} value - Vrije tekst zoals 'Tussenwoning' of 'Flat'
 * @returns {string} Sleutel uit WONINGVORM_OPTIONS, of ''
 */
const parseWoningvorm = (value) => {
    const text = keyOf(value).replace(/\s+/g, ' ')
    if (!text) return ''
    if (text.includes('appartement') || text.includes('flat') || text.includes('portiek') || text.includes('galerij')) return 'appartement'
    if (text.includes('tussen') || text.includes('rijtjes')) return 'tussenwoning'
    if (text.includes('eind') || text.includes('hoek')) return 'eindwoning'
    if (/2[\s-]*onder[\s-]*1|twee[\s-]*onder[\s-]*een/.test(text)) return '2-onder-1-kap'
    if (text.includes('vrijstaand')) return 'vrijstaand'
    return ''
}

/** Alle oude titels van vaste velden, in kleine letters. */
const FIXED_TITLE_KEYS = new Set(Object.values(HOUSEHOLD_FIELDS).flatMap((def) => def.legacyTitles.map(keyOf)))

/**
 * Of een PersonaData-titel een vast gegeven is. Zo'n regel hoort niet meer bij
 * de eigen gegevens: de kaart 'Vaste gegevens' toont hem, en een tweede
 * plek om hem te bewerken zou twee waarheden geven.
 * @param {string|undefined} title
 * @returns {boolean}
 */
const isFixedFieldTitle = (title) => FIXED_TITLE_KEYS.has(keyOf(title))

/**
 * Het vaste veld dat een oude titel vervangt. Een titel die bij twee velden
 * hoort ('Type woning': eigendom én woningvorm) hoort bij het veld waarvan hij
 * de EERSTE oude titel is; anders bij het eerste veld dat hem noemt. Voor een
 * extra kolom in het overzicht die nog op een oude titel staat.
 * @param {string|undefined} title
 * @returns {string|null} Sleutel uit HOUSEHOLD_FIELDS, of null voor een eigen gegeven
 */
const fixedFieldForTitle = (title) => {
    const key = keyOf(title)
    const entries = Object.entries(HOUSEHOLD_FIELDS)
    const first = entries.find(([, def]) => keyOf(def.legacyTitles[0]) === key)
    if (first) return first[0]
    const any = entries.find(([, def]) => def.legacyTitles.some((t) => keyOf(t) === key))
    return any ? any[0] : null
}

/**
 * Een vast gegeven als tekst: een keuze met haar label, een getal als tekst,
 * '' als er niets is. Wat er bij 'niets' moet staan bepaalt de afnemer.
 * @param {string} field - Sleutel uit HOUSEHOLD_FIELDS
 * @param {string|number|null|undefined} value
 * @returns {string}
 */
const formatHouseholdField = (field, value) => {
    if (value === null || value === undefined || value === '') return ''
    const def = HOUSEHOLD_FIELDS[field]
    if (def?.kind === 'enum') return def.options.find((o) => o.value === value)?.label || ''
    return String(value)
}

/**
 * Een waarde zoals hij op het huishouden staat, in de vorm van het veld.
 * @param {HouseholdFieldDef} def
 * @param {*} stored
 * @returns {string|number|null}
 */
const normalizeStored = (def, stored) => {
    if (def.kind === 'number') {
        const n = Number(stored)
        return Number.isFinite(n) && n > 0 ? Math.floor(n) : null
    }
    const text = sentinelToEmpty(stored)
    if (def.kind === 'email') return normalizeEmail(text)
    if (def.kind === 'enum') return def.options.some((o) => o.value === text) ? text : ''
    return text
}

/**
 * Een vast veld uit de oude gegevensregels.
 * @param {string} field
 * @param {Array<{Title?: string, Value?: string}>} rows
 * @returns {string|number|null}
 */
const readLegacy = (field, rows) => {
    const byTitle = valuesByTitle(rows)
    switch (field) {
        case 'Naam': {
            const split = [firstValue(byTitle, ['Voornaam']), firstValue(byTitle, ['Achternaam'])].filter(Boolean).join(' ')
            return split || firstValue(byTitle, ['Naam'])
        }
        case 'Email':
            return resolveHouseholdEmail(rows.map((r) => ({ ...r, Title: HOUSEHOLD_FIELDS.Email.legacyTitles.find((t) => keyOf(t) === keyOf(r?.Title)) || r?.Title })))
        case 'Huisnummer':
            return splitHouseNumber(firstValue(byTitle, ['Huisnummer'])).nummer
        case 'Toevoeging':
            return firstValue(byTitle, ['Toevoeging']) || splitHouseNumber(firstValue(byTitle, ['Huisnummer'])).toevoeging
        case 'AantalBewoners':
            return readResidentCount(rows)
        case 'Eigendom':
            return classifyEigendom(rows).value
        case 'Woningvorm':
            for (const title of HOUSEHOLD_FIELDS.Woningvorm.legacyTitles) {
                for (const value of byTitle.get(keyOf(title)) || []) {
                    const vorm = parseWoningvorm(value)
                    if (vorm) return vorm
                }
            }
            return ''
        case 'Woningcorporatie': {
            const value = firstValue(byTitle, ['Woningbouwcooperatie'])
            return NO_CORPORATION.has(keyOf(value)) ? '' : value
        }
        default:
            return firstValue(byTitle, HOUSEHOLD_FIELDS[field].legacyTitles)
    }
}

/**
 * Leest één vast gegeven van een huishouden.
 *
 * Het veld op het huishouden wint, óók als het leeg is: dan heeft iemand het
 * gewist. `undefined` of `null` betekent 'nog niet overgezet' en dan geldt de
 * oude gegevensregel.
 *
 * @param {object|undefined} adress - Het `Adresses`-document
 * @param {Array<{Title?: string, Value?: string}>|undefined} personaRows - PersonaData van dit huishouden; `undefined` = nog niet geladen
 * @param {string} field - Sleutel uit HOUSEHOLD_FIELDS
 * @returns {string|number|null} De waarde; '' (of null bij een getal) als niets bekend is; null zolang er niets geladen is
 */
const readHouseholdField = (adress, personaRows, field) => {
    const def = HOUSEHOLD_FIELDS[field]
    if (!def) throw new Error(`readHouseholdField: onbekend veld '${field}'`)
    if (!adress) return null

    const stored = adress[field]
    if (stored !== undefined && stored !== null) return normalizeStored(def, stored)

    if (!Array.isArray(personaRows)) return null
    return readLegacy(field, personaRows)
}

/**
 * Het aantal bewoners per huishouden, voor berekeningen over veel huishoudens
 * tegelijk (CO2/€ per bewoner). Het veld op het huishouden, anders de oude
 * regel; ontbrekend is null, nooit 0 (ADR-030). Eén lezing voor browser en
 * server. AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md (T8)
 * @param {Array<{ID?: string}>|undefined} adresses - `Adresses`-documenten (mag alleen ID + AantalBewoners dragen)
 * @param {Array<{PersonaID?: string, Title?: string, Value?: string}>|undefined} rows - PersonaData-regels, verwijderde al gefilterd
 * @returns {Record<string, number|null>}
 */
const residentCountsByAdress = (adresses, rows) => {
    /** @type {Record<string, Array<object>>} */
    const rowsById = {}
    ;(Array.isArray(rows) ? rows : []).forEach((row) => {
        if (!row?.PersonaID) return
        ;(rowsById[row.PersonaID] = rowsById[row.PersonaID] || []).push(row)
    })
    const adressById = {}
    ;(Array.isArray(adresses) ? adresses : []).forEach((adress) => {
        if (adress?.ID) adressById[adress.ID] = adress
    })
    /** @type {Record<string, number|null>} */
    const result = {}
    new Set([...Object.keys(rowsById), ...Object.keys(adressById)]).forEach((id) => {
        result[id] = readHouseholdField(adressById[id] || { ID: id }, rowsById[id] || [], 'AantalBewoners')
    })
    return result
}

export default readHouseholdField
export {
    readHouseholdField,
    HOUSEHOLD_FIELDS,
    EIGENDOM_OPTIONS,
    WONINGVORM_OPTIONS,
    classifyEigendom,
    parseWoningvorm,
    splitHouseNumber,
    isFixedFieldTitle,
    fixedFieldForTitle,
    formatHouseholdField,
    LABEL_LEGACY_TITLES,
    ADDRESS_LABEL_TITLES,
    residentCountsByAdress,
}
